/*!
 * @Author: leo
 * @Date: 2019-12-25 20:55:00
 * @Last Modified by: Robin
 * @Last Modified time: 2024-03-22 14:59:41
 * @Instruduction: 媒体服务接口
 */

import xhr from './xhr/index'
import { BaseParams, DetailParams } from './index'

interface IMediaListParams {
    media_name?: string   // 搜索媒体名
    type?: any            // 分类
    page_index: number    // 第几页
    page_size: number     // 每页条数
    tag_ids?: string  // 标签 ID列表用,分隔
}

interface IGetMediaParams {
    id: number              // 媒体 ID
    media_type?: string
}

interface IDeleteMediaParams {
    id: number   // 媒体 ID
}

interface ICreateMediaParams {
    name: string     // 名称
    region: string   // 区域
    score: number    // 牛媒评分
    url: string      // 官网链接
    logo: string     // logo
    intro: string    // 介绍
    alias: string[]
    media_type?: string // 媒体形态
}

interface INewsListParams {
    media_id: number     // 媒体 ID
    page_index: number   // 第几页
    page_size: number    // 每页条数
}

interface IOrgListParams {
    page_index: number   // 第几页
    page_size: number    // 每页条数
    query?: string
}

interface IOrgDetailParams {
    id: number   // 机构 ID
}

interface IUpdateOrgParams {
    id: number         // 机构 ID
    name: string       // 名称
    url: string        // 官网 URL
    intro: string      // 介绍
    medias: number[]   // 媒体
}

interface ICreateOrgParams {
    name: string       // 名称
    url: string        // 官网 URL
    intro: string      // 介绍
    medias: string[]   // 媒体
}

interface IDeleteOrgParams {
    id: number   // 机构 ID
}

interface ISearchMediaParams {
    value: string   // 关键字
    media_type?: string // 媒体类型
}

interface IPlatformsParams {
    media_id: number   // 媒体 ID
}

interface IUpdateAcctParams {
    account_id: number   // 媒体号 ID
    status: number       // 关联状态
    media_type?: string   // 媒体类型
}

interface IAddPlatformParams {
    mp_id: number
    mp_account_id: string
    media_type?: string
}

interface IGetRelationsParams {
    page_index: number   // 第几页
    page_size: number    // 每页条数
}

interface IRelationship {
    id: number
    name: string
}

interface IAddRelationParams {
    parent_media: object
    child_medias: IRelationship[] 
}

interface IUpdateRelationParams {
    parent_media: object
    child_medias: IRelationship[] 
}

interface IGetRelationParams {
    rel_id: number
}

interface IDeleteRelationParams {
    id: number
}

interface IGetCombinationListParams {
    page_index?: number,
    page_size?: number
}

interface IAddCombinationParams {
    media_list: { id: number, name: string }[]
}

interface IUpdateCombinationParams {
    id: number,
    media_list: { id: number, name: string }[]
}

interface IDeleteCombinationParams {
    id: number
}

interface IGetPhoneListParams {
    status?: number,            // 状态 1:未处理 2:正确 3:错误
    page_index?: number,        // 第几页
    page_size?: number          // 每页条数
}

interface IUpdatePhoneStatusParams {
    id: number,                 // 编号
    status: number              // 状态 1:未处理 2:正确 3:错误
}

interface IGetAuthList extends BaseParams {
    status: number              // 审核状态 1:待审核 2:已通过 3:不通过
}

interface IGetAuthDetail extends DetailParams {}

interface IUpdateAuthStatus extends DetailParams {
    status: number              // 审核状态 1:待审核 2:已通过 3:不通过
}

interface IGetReportList extends BaseParams {
    medium_id?: number | undefined           // 媒体 id
    status?: number | undefined             // 状态 1:未处理 2:核实中 3:已采纳 4:已拒绝
}

interface IGetReportDetail extends DetailParams {}

interface IUpdateReport {
    id: number
    follow_status?: number
    principal?: number
    follow_memo?: string
}

interface IAddSpiderKeyword {
    keyword: string
}

interface IGetTasks {
    keyword: string
    crawlerScript: string
}

class MediaService {
    /** 获取媒体列表 */
    public async getList(params: IMediaListParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/list',
            params: params 
        })
        return result
    }
    /** 获取媒体详情 */
    public async getDetail(params: IGetMediaParams) {
        const result = await xhr({
            method: 'get',
            url: '/media',
            params: params 
        })
        return result
    }
    /** 更新媒体 */
    public async update(params: any) {
        const result = await xhr({
            method: 'put',
            url: '/media',
            params: params 
        })
        return result
    }
    /** 删除媒体 */
    public async remove(params: IDeleteMediaParams) {
        const result = await xhr({
            method: 'delete',
            url: '/media',
            params: params 
        })
        return result
    }
    /** 创建媒体 */
    public async create(params: ICreateMediaParams) {
        const result = await xhr({
            method: 'post',
            url: '/media',
            params: params 
        })
        return result
    }
    /** 获取媒体的新闻列表 */
    public async getNewsList(params: INewsListParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/news_list',
            params: params 
        })
        return result
    }
    /** 搜索媒体 */
    public async searchMedia(params: ISearchMediaParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/search',
            params: params 
        })
        return result
    }
    /** 获取机构列表 */
    public async getOrgList(params: IOrgListParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/org_list',
            params: params 
        })
        return result
    }
    /** 获取机构详情 */
    public async getOrgDetail(params: IOrgDetailParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/org',
            params: params 
        })
        return result
    }
    /** 更新机构 */
    public async updateOrg(params: IUpdateOrgParams) {
        const result = await xhr({
            method: 'put',
            url: '/media/org',
            params: params 
        })
        return result
    }
    /** 创建机构 */
    public async createOrg(params: ICreateOrgParams) {
        const result = await xhr({
            method: 'post',
            url: '/media/org',
            params: params 
        })
        return result
    }
    /** 删除机构 */
    public async removeOrg(params: IDeleteOrgParams) {
        const result = await xhr({
            method: 'delete',
            url: '/media/org',
            params: params 
        })
        return result
    }
    /** 获取媒体平台列表 */
    public async getPlatforms(params: IPlatformsParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/platforms',
            params: params
        })
        return result
    }
    /** 更新媒体号 */
    public async updateAcct(params: IUpdateAcctParams) {
        const result = await xhr({
            method: 'put',
            url: '/media/account',
            params: params
        })
        return result
    }
    /** 添加媒体号 */
    public async addPlatform(params: IAddPlatformParams) {
        const result = await xhr({
            method: 'post',
            url: '/media/account',
            params: params 
        })
        return result
    }
    /** 添加平台号 */
    public async addMpAccount(params: any) {
        const result = await xhr({
            method: 'post',
            url: '/mp_account',
            params: params 
        })
        return result
    }
    /** 获取媒体关系列表 */
    public async getRelations(params: IGetRelationsParams) {
        const result = await xhr({
            method: 'get',
            url: '/media/relationships',
            params: params 
        })
        return result
    }
    /** 创建媒体关系 */
    public async createRelation(params: IAddRelationParams) {
        const result = await xhr({
            method: 'post',
            url: '/media/relationship',
            params: params 
        })
        return result
    }
    /** 编辑媒体关系 */
    public updateRelation(params: IUpdateRelationParams) {
        return xhr({
            method: 'put',
            url: '/media/relationship',
            params: params 
        })
    }
    /** 编辑媒体关系 */
    public async getRelation(params: IGetRelationParams) {
        return xhr({
            method: 'get',
            url: '/media/relationship',
            params: params 
        })
    }
    /** 删除媒体关系 */
    public deleteRelation(params: IDeleteRelationParams) {
        return xhr({
            method: 'delete',
            url: '/media/relationship',
            params: params 
        })
    }
    /** 获取共用记者列表 */
    public getCombinationList(params: IGetCombinationListParams) {
        return xhr({
            method: 'get',
            url: '/media/combination_list',
            params: params
        })
    }
    /** 创建组合(共用记者) */
    public addCombination(params: IAddCombinationParams) {
        return xhr({
            method: 'post',
            url: '/media/combination',
            params: params
        })
    }
    /** 修改组合(共用记者) */
    public updateCombination(params: IUpdateCombinationParams) {
        return xhr({
            method: 'put',
            url: '/media/combination',
            params: params
        })
    }
    /** 删除组合(共用记者) */
    public deleteCombination(params: IDeleteCombinationParams) {
        return xhr({
            method: 'delete',
            url: '/media/combination',
            params: params
        })
    }
    /** 获取联系热线列表 */
    public getPhoneList(params: IGetPhoneListParams) {
        return xhr({
            method: 'get',
            url: '/media/add_phone/list',
            params: params
        })
    }
    /** 修改联系热线审核状态 */
    public updatePhoneStatus(params: IUpdatePhoneStatusParams) {
        return xhr({
            method: 'put',
            url: '/media/add_phone',
            params: params
        })
    }

    /** 认证申请列表 */
    public getAuthList(params: IGetAuthList) {
        return xhr({
            method: 'get',
            url: '/media/cert/list',
            params: params
        })
    }

    /** 认证申请详情 */
    public getAuthDetail(params: IGetAuthDetail) {
        return xhr({
            method: 'get',
            url: '/media/cert',
            params: params
        })
    }

    /** 审核认证申请 */
    public updateAuthStatus(params: IUpdateAuthStatus) {
        return xhr({
            method: 'put',
            url: '/media/cert',
            params: params
        })
    }

    /* 报料列表 */
    public getReportList(params: IGetReportList) {
        return xhr({
            method: 'get',
            url: '/media/report/list',
            params: params
        })
    }

    /* 报料详情 */
    public getReportDetail(params: IGetReportDetail) {
        return xhr({
            method: 'get',
            url: '/media/report',
            params: params
        })
    }

    /* 更新报料 */
    public updateReport(params: IUpdateReport) {
        return xhr({
            method: 'put',
            url: '/media/report',
            params: params
        })
    }

    /* 收录审核列表 */
    public getStoreList(params: any) {
        return xhr({
            method: 'get',
            url: '/media_check/list',
            params: params
        })
    }

    /* 收录审核列表 */
    public updateStoreStatus(params: any) {
        return xhr({
            method: 'put',
            url: '/media_check',
            params: params
        })
    }

    /* 获取收录审核详情 */
    public getStoreDetail(params: any) {
        return xhr({
            method: 'get',
            url: '/media_check',
            params: params
        })
    }

    /* 补充文章 */
    public createMediaNews(params: any) {
        return xhr({
            method: 'post',
            url: '/media/news',
            params: params
        })
    }
    /* 媒体属性列表 */
    public getMediaTypes(params?: any) {
        return xhr({
            method: 'get',
            url: '/media/types',
            params: params
        })
    }
    /* 媒体形态列表 */
    public getMediaMpTypes(params?: any) {
        return xhr({
            method: 'get',
            url: '/media/mp_types',
            params: params
        })
    }
    /* 媒体添加媒体形态  */
    public addMediaMpTypes(params: any) {
        return xhr({
            method: 'post',
            url: '/media/mp_type',
            params: params
        })
    }
    /* 媒体修改媒体形态  */
    public updateMediaMpTypes(params: any) {
        return xhr({
            method: 'put',
            url: '/media/mp_types',
            params: params
        })
    }
    /* 导出媒体报料  */
    public exportMediaReports(params: any) {
        return xhr({
            method: 'post',
            url: '/media/report/export',
            params
        })
    }
    public addSpiderKeyword(params: IAddSpiderKeyword) {
        return xhr({
            method: 'post',
            url: '/spider_keyword',
            params
        })
    }
    public async getTasks(params: IGetTasks) {
        const result = await xhr({
            method: 'get',
            url: '/get_tasks',
            params: params 
        })
        return result
    }
    public async getMpTypeGroup() {
        const result = await xhr({
            method: 'get',
            url: '/media/mp_type_group',
            params: {}
        })
        return result
    }
    public async updateMpAccountMemo(params: { mp_id: number, data: string }) {
        return xhr({
            method: 'post',
            url: '/media/update_mp_account_memo',
            params
        })
    }
    public async updateMediaPlatformBindingStatus(params: {
        medium_id: number;
        binding_status_id: number;
        binding_status_name: string;
        binding_status: string;
    }) {
        return xhr({
            method: 'post',
            url: '/media/update_binding_status',
            params
        })
    }
    /**
     * 发起一次媒体信源存在性 AI 校验
     * 接口：GET /v1/media/ai/once
     */
    public async triggerAiCheck(params: {
        mediaId: string
        sourceGroupId: string
        sourceId: string
    }) {
        return xhr({
            method: 'get',
            url: '/media/ai/once',
            params
        })
    }
    /**
     * 获取媒体节点的 AI 校验历史记录
     * 接口：GET /v1/media/ai/analysis/list?mediaId=&sourceGroupId=&sourceId=
     */
    public async getAnalysisList(params: {
        mediaId: string
        sourceGroupId: string
        sourceId: string
    }) {
        return xhr({
            method: 'get',
            url: '/media/ai/analysis/list',
            params
        })
    }
}

// 实例化后导出，全局单例
export default new MediaService()
